import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, forkJoin } from 'rxjs';
import { delay, switchMap, tap } from 'rxjs/operators';
import { ApplicationBaseComponent } from '../../../../applications/components/application-base.component';
import { ApplicationState, ApplicationStateChangeRequest, WorkflowData } from '../../../../applications/models/applications.models';
import { ApplicationNotesService } from '../../../../applications/services/application-notes.service';
import { ApplicationsService } from '../../../../applications/services/applications.service';
import { DomainDefinitionService } from '../../../../builder/services/domain-definition.service';
import { OrganizationStub } from '../../../../common/models/organization.model';
import { Well } from '../../../../common/models/well.model';
import { LoaderService } from '../../../../common/services/loader.service';
import { SidePanelService } from '../../../../common/services/side-panel.service';
import { WellService } from '../../../../common/services/well.service';
import { Dialog, DialogButton } from '../../../../core/models/dialog.model';
import { SubHeader } from '../../../../core/models/sub-header.model';
import { DynamicDialogService } from '../../../../core/services/dynamic-dialog.service';
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { FormationModel } from '../../../../form-controls/components/new-casing-plan/models/casing-plan.model';
import { ApplicationConfigService, ModuleConfig } from '../../../../services/application-config.service';
import { AuthService } from '../../../../services/auth.service';
import { ComplianceReportingType } from '../../../models/compliance.model';
import { WellSelectorComponent } from '../../well-selector/well-selector.component';
import { CasingTypeModel } from "../../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { OnBaseDocumentTypeProcessReference, OnBaseSubProcessName } from '../../../../common/models/onBaseIntegrationAndMapping.model';

@Component({
    selector: 'app-preliminary-completion-report-review',
    templateUrl: './preliminary-completion-report-review.component.html',
    styleUrls: ['./preliminary-completion-report-review.component.scss'],
    standalone: false
})
export class PreliminaryCompletionReportReviewComponent extends ApplicationBaseComponent implements OnInit {

    @Input() reportType: ComplianceReportingType;
    @Input() well: Well;
    @Input() organization: OrganizationStub;
    @Input() application: WorkflowData;
    @Input() onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];
	_validStringCasingTypeCodes = ['DP*', 'COND', 'MS1', 'SURF', 'I1', 'I2', 'PROD', 'PROD2', 'HOL1', 'PKR', 'L1', 'T1'];

    get selectedWell(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('apiNumber');
        if (ctrl == null) return "";
        return ctrl.value;
    }

    get isInternal(): boolean {
        return this.auth.User.isInternal;
    }

	get wellIsSelected(): boolean {
		return this.selectedWell != null;
	}


	formations: FormationModel[];
	maxFormations: number = 15;		

	get loadComplete(): boolean {
		return this.loading$ != null && this.loading$.value == false;
	}

    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    loadingObs$: Observable<boolean> = this.loading$.asObservable();

    get isInCorrections(): boolean {
        if (this.application == null) return false;
        return this.application.applicationState == ApplicationState.Corrections;
    }

    get isApproved(): boolean {
        if (this.application == null) return false;
        return this.application.applicationState == ApplicationState.Approved;
    }
	private _itemList: number[] = undefined;
	get itemList(): number[] {
		if (this._itemList == null) {
			this._itemList = [];
			for (let i = 0; i < this.maxFormations; i++) {
				this._itemList.push(i);
			}
		}
		return this._itemList;
	}

	methods: any[] = [];
    selectedFormations: any[] = [];

	dataTypes: CasingTypeModel[] = [];

    get subheaderText(): string {
        return "Compliance Report #[XXX]";
    }

    subheader: SubHeader = new SubHeader({ title: "", buttons: [] });



    acceptedDialog: Dialog = new Dialog("Confirm that you are accepting this application?", [new DialogButton("Confirm", true, () => { this.onAcceptConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    returnedDialog: Dialog = new Dialog("Return this application for corrections?", [new DialogButton("Confirm", true, () => { this.onReturnConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    acceptConfirmedDialog: Dialog = new Dialog("The application has been accepted.", [new DialogButton("OK", true, () => { this.uploadDocumentsAndGoHome(); })]);
    returnedConfirmedDialog: Dialog = new Dialog("The application has been returned for corrections.", [new DialogButton("OK", true, () => { this.home(); })]);
    savedDialog: Dialog = new Dialog("Save the review and return to the homepage?", [new DialogButton("Yes", true, () => { this.onSaveConfirmed(); }), new DialogButton("No", true, () => { }),]);
    saveConfirmedDialog: Dialog = new Dialog("The review has been saved.", [new DialogButton("OK", true, () => { this.home(); })]);
    canceledDialog: Dialog = new Dialog("Leave application unsaved and return to home page?", [new DialogButton("Yes", true, () => { this.home(); }), new DialogButton("No", true, () => { }),]);

    constructor(loader: LoaderService, dialog: DynamicDialogService, public _subheader: SubHeaderService, auth: AuthService, public appService: ApplicationsService, private configService: ApplicationConfigService,
        router: Router, sidePanelSvc: SidePanelService, private domainService: DomainDefinitionService, noteSvc: ApplicationNotesService, private builder: FormBuilder, public wellService: WellService) {
        super(loader, dialog, _subheader, auth, appService, router, sidePanelSvc, noteSvc);


        this.numericFields = ['ownerWellInfo|surfaceLat', 'ownerWellInfo|surfaceLong', 'ownerWellInfo|surfaceLong', 'wellInfo|drillerTotalDepth', 'wellInfo|loggerTotalDepth', 'wellInfo|groundLevel', 'wellInfo|derrickFloor', 'wellInfo|kellyBushing', 'completionInfo|shotLbs', 'completionInfo|shotQts', 'completionInfo|acidGals', 'completionInfo|acidPercent',
            'completionInfo|waterGals', 'completionInfo|waterBbl', 'completionInfo|fracCos', 'completionInfo|fracN2', 'completionInfo|sandLbs', 'completionInfo|sandSks', 'completionInfo|pressureBreakdown', 'completionInfo|pressureATP', 'completionInfo|pressureISIP', 'completionInfo|pressureSIP', 'completionInfo|pressureAvg']
        this.trueBooleanFields = ['ownerWellInfo|wellNotDrilled']
        this.fileFields = ['documentation|cementTickets', 'documentation|cementJobLogs', 'documentation|safetyDateSheet', 'documentation|cementData', 'documentation|prGraph', 'documentation|stimInvoice',
            'documentation|stimRecord', 'documentation|stimLog',]
        this.arrayFields = ['formations|selectedFormations']
        this.dateFields = ['wellInfo|drillingStartDate', 'wellInfo|drillingEndDate', 'receivedDates|formReceivedDate', 'receivedDates|cementTickReceivedDate', 'receivedDates|cementLogReceivedDate', 'receivedDates|safetySheetReceivedDate', 'receivedDates|prGraphReceivedDate', 'receivedDates|stimInvoiceDateReceived', 'receivedDates|stimRecordReceivedDate', 'receivedDates|stimLogReceivedDate',]
    }


    ngOnInit() {
		const casingString$: Observable<any> = this.domainService.getValues("CSG_STRING").pipe(
			tap((casings: any[]) => {
				if (casings != null) {
					let unsorted: CasingTypeModel[] = [];
					casings.forEach(c => {
						if (this._validStringCasingTypeCodes.includes(c.Code)) {
							const model = new CasingTypeModel();
							model.code = c.Code;
							model.definition = (c.Code !== 'HOL1') ? c.Definition : 'Open Hole';
							model.isActive = c.isActive;
							model.isRequired = c.isRequired;
							model.lastModifiedDateUTC = c.LastModifiedDateUTC;
							if (model.definition.toLocaleLowerCase().endsWith('casing')) {
								model.definition = model.definition.substring(0, model.definition.length - 6);
							}
							unsorted.push(model);
						}
					});
					this._validStringCasingTypeCodes.forEach(code => {
						let model = unsorted.find(m => m.code === code);
						if (model != null) {
							this.dataTypes.push(model);
						}
					})
				}
				
				this.form = this.buildForm();
				console.log('form build in review form', this.form);
			})
		);
		let conf$: Observable<ModuleConfig> = this.configService.getModuleConfig().pipe(
			tap((config: ModuleConfig) => {
				if (config.postDrilling != null) {
					this._validStringCasingTypeCodes = config.postDrilling.validCasingTypes;
					this.maxFormations = config.postDrilling.maximumFormations;
				}
			}),
			switchMap(() => {
				return casingString$;
			})
		);
       this.application = this.application;

        let obs$: Observable<FormationModel[]> = this.domainService.getFormations().pipe(
            tap((models: FormationModel[]) => {
                this.formations = models;
            })

        );

        this._subheader.updateSubHeader(this.subheader);
		const method$: Observable<any> = this.domainService.getValues("METH_OBTND").pipe(
			tap((data: any[]) => {
				this.methods = data;
			})
		);
		let subs$: Subscription = forkJoin(conf$, obs$, method$).subscribe(
			() => {
				if (this.application != null) {
					this.form = this.restoreFormData(this.form);
					this.form.disable();
					this.form.get('userUpdates').enable();
					this.form.get('receivedDates').enable();
					console.log('form restored in review form', this.form);
					this.subheader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber);
					this.subheader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });
					this.subheaderService.updateSubHeader(this.subheader);
					this.loading$.next(false);

				}
			},
			(err) => {
				console.error(err);
				this.dialogService.showError();
			}
		)
		this.subscription.add(subs$);    
    }

    ngAfterViewInit() {

    }
    buildForm(): FormGroup {
        return this.builder.group({
            wellName: ["", []],
            wellNumber: ["", []],
            apiNumber: ["", []],
            organizationName: [this.application.workflowFieldDatas.find(f => f.formControlName == "organizationName"), []],
            organization: [this.application.workflowFieldDatas.find(f => f.formControlName == "organization"), []],
            rbdmsNumber: [this.application.workflowFieldDatas.find(f => f.formControlName == "rbdmsNumber"), []],
            reportType: [this.reportType.typeId, []],
            selectedWell: [null, [Validators.required]],
            ownerWellInfo: this.builder.group({
                ownerNumber: [{ value: null, disabled: true }, []],
                ownerName: [{ value: null, disabled: true }, []],
                apiNumber: [{ value: null, disabled: true }, []],
                wellName: [{ value: null, disabled: true }, []],
                wellNumber: [{ value: null, disabled: true }, , []],
                surfaceLat: [{ value: null, disabled: true }, []],
                surfaceLong: [{ value: null, disabled: true }, []],
                county: [{ value: null, disabled: true }, []],
                township: [{ value: null, disabled: true }, []],
                quadrangle: [{ value: null, disabled: true }, []],
                section: [{ value: null, disabled: true }, []],
                lot: [{ value: null, disabled: true }, []],
                tract: [{ value: null, disabled: true }, []],
                typeOfWell: [{ value: null, disabled: true }, []],
                wellNotDrilled: [{ value: null, disabled: true }, []],
            }),
            wellInfo: this.builder.group({
                drillingStartDate: [null, []],
                drillingEndDate: [null, []],
                deepestFormation: [null, []],
                drillerTotalDepth: [null, []],
                loggerTotalDepth: [null, []],
                drillingContractor: [null, []],
                groundLevel: [null, []],
                derrickFloor: [null, []],
                kellyBushing: [null, []]
            }),
            documentation: this.builder.group({
                cementTickets: [[], []],
                cementJobLogs: [[], []],
                safetyDateSheet: [[], []],
                cementData: [[], []],
                prGraph: [[], []],
                stimInvoice: [[], []],
                stimRecord: [[], []],
                stimLog: [[], []],

            }),
			formations: this.buildFormationsForm(),
            casingTubing: this.buildCasingTubingForm(),
            userUpdates: this.builder.group({
                disposition: [null, []],
                category: [null, []],
                wellSymbol: [null, []],
                wellTypeDesc: [null, []],
                legacyTracking: [null, []]
            }),
            receivedDates: this.builder.group({
                formReceivedDate: [null, []],
                cementTickReceivedDate: [null, []],
                cementLogReceivedDate: [null, []],
                safetySheetReceivedDate: [null, []],
                prGraphReceivedDate: [null, []],
                stimInvoiceDateReceived: [null, []],
                stimRecordReceivedDate: [null, []],
                stimLogReceivedDate: [null, []],
            }),
            certifications: this.builder.group({
                submitterName: [{ value: null, disabled: true }],
                submitterTitle: [{ value: null, disabled: true }],
            })



        });
    }



    buildCasingTubingForm(): FormGroup {
        let obj: any = {};
        this.dataTypes.forEach((type) => {
			obj[type.code] = this.buildSubCasingTubingForm();
			this.numericFields.push(`casingTubing|${type.code}|wellboreDiameter`);
			this.numericFields.push(`casingTubing|${type.code}|casingDiameter`);
			this.numericFields.push(`casingTubing|${type.code}|collarDiameter`);
			this.numericFields.push(`casingTubing|${type.code}|feetInstalled`);
			this.numericFields.push(`casingTubing|${type.code}|feetLeftInWell`);
			this.numericFields.push(`casingTubing|${type.code}|cementVolume`);
			this.numericFields.push(`casingTubing|${type.code}|cementTop`);
			this.numericFields.push(`casingTubing|${type.code}|grade`);
			this.numericFields.push(`casingTubing|${type.code}|weight`);
			this.numericFields.push(`casingTubing|${type.code}|bottom`);
			this.numericFields.push(`casingTubing|${type.code}|burstPressureRating`);

        });
        return this.builder.group(obj);
    }

    buildSubCasingTubingForm(): FormGroup {
        let subObj: any = {};
		subObj["wellboreDiameter"] = [{ value: null, disabled: true }, []];
		subObj["casingDiameter"] = [{ value: null, disabled: true }, []];
		subObj["collarDiameter"] = [{ value: null, disabled: true }, []];
		subObj["feetInstalled"] = [{ value: null, disabled: true }, []];
		subObj["feetLeftInWell"] = [{ value: null, disabled: true }, []];
		subObj["cementVolume"] = [{ value: null, disabled: true }, []];
		subObj["cementTop"] = [{ value: null, disabled: true }, []];
		subObj["grade"] = [{ value: null, disabled: true }, []];
		subObj["weight"] = [{ value: null, disabled: true }, []];
		subObj["bottom"] = [{ value: null, disabled: true }, []];
		subObj["burstPressureRating"] = [{ value: null, disabled: true }, []];
		subObj["toolType"] = [{ value: null, disabled: true }, []];


        return this.builder.group(subObj);
	}

	buildFormationsForm(): FormGroup {
		let formationForm: any = {};
		for (let f = 0; f < this.maxFormations; f++) {
			formationForm[`selectedFormation${f}`] = [{ value: null, disabled: true }, []];
			formationForm[`top${f}`] = [{ value: null, disabled: true }, []];
			formationForm[`base${f}`] = [{ value: null, disabled: true }, []];
			formationForm[`showsOf${f}`] = [{ value: null, disabled: true }, []];
			formationForm[`methodObtained${f}`] = [{ value: null, disabled: true }, []];
			formationForm[`producingFormation${f}`] = [{ value: false, disabled: true }, []];
			formationForm[`remarks${f}`] = [{ value: null, disabled: true }, []];
			formationForm[`aapgCode${f}`] = [{ value: null, disabled: false }, []];
			this.trueBooleanFields.push(`formations|producingFormation${f}`);
			this.numericFields.push(`formations|top${f}`);
			this.numericFields.push(`formations|base${f}`);
		}
		return this.builder.group(formationForm);
	}


    onWellsSelected(evt: Well[]): void {
        let selectedWell = this.form.get('apiNumber');
        if (evt.length != 0) {

            let wellName: string = evt[0].name;
            if (evt[0].lease != null && evt[0].lease.name != null && evt[0].lease.name.length > 0) wellName = evt[0].lease.name;
            else if (evt[0].wellNumber != null && evt[0].wellNumber.length > 0 && wellName != null && wellName.trim().endsWith(evt[0].wellNumber.trim())) {
                wellName = wellName.trim().replace(evt[0].wellNumber.trim(), "");
            }

            this.form.get('wellNumber').setValue(evt[0].wellNumber);
            this.form.get('wellName').setValue(wellName);
        }

        let update: boolean = false;
        evt.forEach(w => {
            if (w.id != selectedWell.value) {
                this.form.get('apiNumber').setValue(w.id);

                update = true;
            }
        });
        if (update) {
            if (evt.length == 0) {
                this.form.get('apiNumber').setValue(null);

            }
        }
    }

    cancel(): void {
        this.canceledDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.canceledDialog);
    }

    onSave(): void {

        this.savedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.savedDialog);

    }


        onSaveConfirmed(): void {
            let obs$: Observable < WorkflowData > = this.saveProgress();
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this._loader.toggleLoader(false);
                    this.saveConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.saveConfirmedDialog
                    );

                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$);

    }

    onAccept(): void {

        this.acceptedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.acceptedDialog);
    }

    onAcceptConfirmed() {
        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {

                let destination: ApplicationState = ApplicationState.Approved;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this._loader.toggleLoader(false);
                    this.acceptConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.acceptConfirmedDialog);

                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$);
    }

    uploadDocumentsAndGoHome(): void {
        let upload$: Subscription = this.dataService.commitAssociatedDocuments(this.application.applicationNumber, false).pipe()
            .subscribe(
                () => { },
                (error) => {
                    this.handleError(error);
                }
            );
        this.subscription.add(upload$);
        setTimeout(() => { this.home(); }, 1500); //timeout before redirecting to give subscription a chance to fire
    }

    onReturn() {
        this.returnedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.returnedDialog);

    }

    onReturnConfirmed() {

        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.Corrections;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this._loader.toggleLoader(false);
                    this.returnedConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.returnedConfirmedDialog);


                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$)

    }

}
